import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Factory, Plus, Pencil, Trash2 } from "lucide-react";
import { fmtIN } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

const MACHINE_TYPES = ["LASER", "CNC_PRESS_BRAKE", "ROBOT_WELDER", "MIG_WELDER", "DRILL", "BANDSAW", "PRESS"];
const SHIFT_PATTERNS = ["SINGLE_SHIFT", "TWO_SHIFT"];

export default function Machines() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [plants, setPlants] = useState([]);
    const [error, setError] = useState("");
    const [plantFilter, setPlantFilter] = useState("ALL");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // machine being edited (null = add)
    const emptyForm = {
        code: "", name: "", type: "LASER", plant_id: "", capacity_per_hour: 0,
        shift_pattern: "SINGLE_SHIFT", shift_hours_per_day: 8, is_active: true,
    };
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        Promise.all([api.get("/machines"), api.get("/plants")])
            .then(([m, p]) => { setRows(m.data); setPlants(p.data); })
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const plantMap = useMemo(() => Object.fromEntries(plants.map((p) => [p.id, p])), [plants]);
    const filtered = useMemo(() => {
        if (!rows) return null;
        if (plantFilter === "ALL") return rows;
        return rows.filter((r) => r.plant_id === plantFilter);
    }, [rows, plantFilter]);

    const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (m) => {
        setEditing(m);
        setForm({
            code: m.code || "", name: m.name || "", type: m.type || "LASER",
            plant_id: m.plant_id || "",
            capacity_per_hour: m.capacity_per_hour ?? 0,
            shift_pattern: m.shift_pattern || "SINGLE_SHIFT",
            shift_hours_per_day: m.shift_hours_per_day ?? 8,
            is_active: m.is_active !== false,
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                capacity_per_hour: parseFloat(form.capacity_per_hour) || null,
                shift_hours_per_day: parseFloat(form.shift_hours_per_day) || 8,
            };
            if (editing) {
                // Code is locked once created — send only mutable fields.
                const { code: _code, ...rest } = payload;
                await api.put(`/machines/${editing.id}`, rest);
                toast.success(`${editing.code} updated`);
            } else {
                await api.post("/machines", payload);
                toast.success("Machine added");
            }
            setOpen(false);
            setForm(emptyForm);
            setEditing(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete machine ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/machines/${row.id}`);
            toast.success("Machine deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.machinesPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 4"
                title="Machines"
                subtitle={user.role === "PLANT_MANAGER"
                    ? "Machines assigned to your plant. Submit hourly rates for Corporate Admin approval before they can be used in ZBCs."
                    : "Fleet across all 12 plants. Each machine needs an approved hourly rate before it can be used in costing."}
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="machines" label="Machines" canImport={canEdit} onImported={load} />
                        {user.role !== "PLANT_MANAGER" && (
                            <Select value={plantFilter} onChange={(e) => setPlantFilter(e.target.value)} className="w-48">
                                <option value="ALL">All plants</option>
                                {plants.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.city}</option>)}
                            </Select>
                        )}
                        {canEdit && (
                            <CopperButton onClick={openAdd} data-testid={TID.machinesAddBtn}>
                                <Plus className="h-4 w-4" /> Add Machine
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {filtered && filtered.length === 0 && <EmptyState icon={Factory} title="No machines yet" />}

            {filtered && filtered.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                    <table data-testid={TID.machinesTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Machine</th>
                                <th className="eyebrow text-left py-2 px-4">Type</th>
                                <th className="eyebrow text-left py-2 px-4">Plant</th>
                                <th className="eyebrow text-right py-2 px-4">Capacity (/hr)</th>
                                <th className="eyebrow text-left py-2 px-4">Shift Pattern</th>
                                <th className="eyebrow text-right py-2 px-4">Effective Daily Capacity</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((m) => {
                                const plant = plantMap[m.plant_id];
                                return (
                                    <tr key={m.id} className="border-b border-slate-100">
                                        <td className="py-1.5 px-4 text-[13px] font-mono font-semibold">{m.code}</td>
                                        <td className="py-1.5 px-4 text-[13px]">{m.name}</td>
                                        <td className="py-1.5 px-4"><Pill tone="slate">{m.type.replace(/_/g, " ")}</Pill></td>
                                        <td className="py-1.5 px-4 text-[12px] text-slate-600">{plant ? `${plant.code} · ${plant.city}` : "—"}</td>
                                        <td className="py-1.5 px-4 text-[13px] font-mono tabular-nums text-right">{m.capacity_per_hour ? fmtIN(m.capacity_per_hour, { decimals: 1 }) : "—"}</td>
                                        <td className="py-1.5 px-4">
                                            <Pill tone={m.shift_pattern === "TWO_SHIFT" ? "copper" : "slate"}>
                                                {(m.shift_pattern || "SINGLE_SHIFT").replace("_", " ")} · {m.shift_hours_per_day ?? 8}h
                                            </Pill>
                                        </td>
                                        <td className="py-1.5 px-4 text-[13px] font-mono tabular-nums text-right">{m.effective_daily_capacity ? fmtIN(m.effective_daily_capacity, { decimals: 1 }) : "—"}</td>
                                        <td className="py-1.5 px-4"><Pill tone={m.is_active ? "emerald" : "slate"}>{m.is_active ? "Active" : "Inactive"}</Pill></td>
                                        <td className="py-1.5 px-4 text-right">
                                            {canEdit && (
                                                <div className="inline-flex gap-1">
                                                    <SlateButton
                                                        onClick={() => openEdit(m)}
                                                        className="h-7 px-2.5 text-xs"
                                                        data-testid={`machine-edit-${m.id}`}
                                                        title={`Edit ${m.code}`}
                                                    >
                                                        <Pencil className="h-3 w-3" /> Edit
                                                    </SlateButton>
                                                    <SlateButton
                                                        onClick={() => remove(m)}
                                                        variant="ghost"
                                                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                        title={`Delete ${m.code}`}
                                                        data-testid={`machine-delete-${m.id}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </SlateButton>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit · ${editing.code}` : "Add Machine"} size="lg"
                footer={<>
                    <SlateButton variant="outline" onClick={() => setOpen(false)}>Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={submitting}>{submitting ? "Saving…" : editing ? "Save Changes" : "Add Machine"}</CopperButton>
                </>}>
                <form onSubmit={submit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Machine Code" required helper={editing ? "Code is locked once created" : undefined}>
                            <TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LASER-06" required disabled={!!editing} />
                        </Field>
                        <Field label="Type" required>
                            <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                                {MACHINE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <Field label="Machine Name" required>
                        <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Trumpf TruLaser 5040" required />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Plant" required>
                            <Select value={form.plant_id} onChange={(e) => setForm({ ...form, plant_id: e.target.value })} required>
                                <option value="">Select plant…</option>
                                {plants.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                            </Select>
                        </Field>
                        <Field label="Capacity (units/hr)">
                            <NumberInput value={form.capacity_per_hour} onChange={(e) => setForm({ ...form, capacity_per_hour: e.target.value })} step="0.1" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Shift Pattern" helper="How many shifts this machine actually runs per day">
                            <Select value={form.shift_pattern} onChange={(e) => setForm({ ...form, shift_pattern: e.target.value })}>
                                {SHIFT_PATTERNS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                            </Select>
                        </Field>
                        <Field label="Shift Hours/Day">
                            <NumberInput value={form.shift_hours_per_day} onChange={(e) => setForm({ ...form, shift_hours_per_day: e.target.value })} step="0.5" min="0" />
                        </Field>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 text-[11.5px] text-slate-500">
                        Capacity per hour × shift hours × shifts gives an <strong>Effective Daily Capacity</strong> shown
                        on the Machines list — a planning reference only. It never changes this machine's approved
                        Machine Rate ₹/hr.
                    </div>
                    {editing && (
                        <Field label="Active">
                            <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="h-4 w-4 rounded border-slate-300 accent-[hsl(var(--copper))]"
                                />
                                {form.is_active ? "Machine is active" : "Machine is inactive (hidden from new ZBCs)"}
                            </label>
                        </Field>
                    )}
                </form>
            </Modal>
        </div>
    );
}